import { router } from "@inertiajs/react";
import { AlertDialog, Badge, Button, Card, Flex, Grid, IconButton, Inset, Text } from "@radix-ui/themes";
import { Trash2 } from "lucide-react";
import { ManagerGalleryImage } from "@/types/manager_gallery";
import ReorderControls from "./ReorderControls";
import { reorderedIds } from "./ordering";
import { humanSize } from "./format";

interface Props {
  images: ManagerGalleryImage[];
  reorderPath: string;
}

export default function ImageGrid({ images, reorderPath }: Props) {
  const moveTo = (from: number, to: number) => {
    router.patch(
      reorderPath,
      { ordered_ids: reorderedIds(images, from, to) },
      { preserveScroll: true },
    );
  };

  if (images.length === 0) {
    return (
      <Card>
        <Text size="2" color="gray">
          No images yet. Add some above and they'll appear here in upload order.
        </Text>
      </Card>
    );
  }

  return (
    <Grid columns={{ initial: "2", sm: "3", md: "4" }} gap="3">
      {images.map((image, index) => (
        <Card key={image.id}>
          <Inset clip="padding-box" side="top" pb="current">
            {/* The thumbnail is a variant; the link behind it is the original,
                so a curator can check a frame at full size without the grid
                pulling megabytes per tile. */}
            <a href={image.full_url ?? undefined} target="_blank" rel="noreferrer">
              <img
                src={image.thumb_url ?? image.full_url ?? undefined}
                alt={image.filename}
                loading="lazy"
                style={{
                  display: "block",
                  objectFit: "cover",
                  width: "100%",
                  height: 140,
                  backgroundColor: "var(--gray-3)",
                }}
              />
            </a>
          </Inset>

          <Flex direction="column" gap="2">
            <Flex align="center" gap="2" justify="between">
              <Badge variant="soft" color="gray" size="1">
                {index + 1}
              </Badge>
              <Text size="1" color="gray">
                {humanSize(image.byte_size)}
              </Text>
            </Flex>

            <Text size="1" color="gray" truncate title={image.filename}>
              {image.filename}
            </Text>

            <Flex align="center" justify="between" gap="2">
              <ReorderControls
                index={index}
                total={images.length}
                onMove={(to) => moveTo(index, to)}
              />
              <AlertDialog.Root>
                <AlertDialog.Trigger>
                  <IconButton size="1" variant="soft" color="red" aria-label="Delete image">
                    <Trash2 size={13} />
                  </IconButton>
                </AlertDialog.Trigger>
                <AlertDialog.Content maxWidth="400px">
                  <AlertDialog.Title>Delete this image?</AlertDialog.Title>
                  <AlertDialog.Description size="2">
                    {image.filename} will be removed from the gallery and from storage.
                    This cannot be undone.
                  </AlertDialog.Description>
                  <Flex gap="3" mt="4" justify="end">
                    <AlertDialog.Cancel>
                      <Button variant="soft" color="gray">
                        Keep
                      </Button>
                    </AlertDialog.Cancel>
                    <AlertDialog.Action>
                      <Button
                        variant="solid"
                        color="red"
                        onClick={() =>
                          router.delete(image.destroy_path, { preserveScroll: true })
                        }
                      >
                        Delete
                      </Button>
                    </AlertDialog.Action>
                  </Flex>
                </AlertDialog.Content>
              </AlertDialog.Root>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Grid>
  );
}
